import React from 'react';
import { Button as MuiButton, CircularProgress, styled } from '@mui/material';
import type { ButtonProps as MuiButtonProps } from '@mui/material';
import { alpha } from '@mui/material/styles';

// Define custom button variants (includes standard MUI variants)
export type ButtonVariant = 'contained' | 'outlined' | 'text' | 'primary' | 'secondary' | 'danger';
export type ButtonSize = 'small' | 'medium' | 'large';

// Extended props interface
export interface CustomButtonProps extends Omit<MuiButtonProps, 'variant' | 'size'> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  loading?: boolean;
  loadingText?: string;
  fullWidth?: boolean;
  icon?: React.ReactNode;
  iconPosition?: 'start' | 'end';
  customVariant?: ButtonVariant; // For internal styled component usage
}

// Styled button with custom variants
const StyledButton = styled(MuiButton, {
  shouldForwardProp: prop =>
    ![
      'loading',
      'loadingText',
      'icon',
      'iconPosition',
      'variant',
      'size',
      'customVariant',
    ].includes(prop as string),
})<CustomButtonProps>(({ theme, customVariant, size, fullWidth, loading }) => {
  // Size configurations
  const sizeConfig = {
    small: {
      height: { xs: 32, sm: 36 },
      padding: { xs: theme.spacing(0.5, 1.5), sm: theme.spacing(0.5, 2) },
      fontSize: { xs: '0.75rem', sm: '0.75rem' },
      minWidth: { xs: 56, sm: 64 },
    },
    medium: {
      height: { xs: 40, sm: 44 },
      padding: { xs: theme.spacing(1, 2), sm: theme.spacing(1, 3) },
      fontSize: { xs: '0.875rem', sm: '0.875rem' },
      minWidth: { xs: 72, sm: 80 },
    },
    large: {
      height: { xs: 48, sm: 52 },
      padding: { xs: theme.spacing(1.5, 3), sm: theme.spacing(1.5, 4) },
      fontSize: { xs: '1rem', sm: '1rem' },
      minWidth: { xs: 100, sm: 120 },
    },
  };

  const currentSize = sizeConfig[size || 'medium'];

  // Base styles
  const baseStyles = {
    height: currentSize.height,
    padding: currentSize.padding,
    fontSize: currentSize.fontSize,
    minWidth: fullWidth ? '100%' : currentSize.minWidth,
    fontWeight: 500,
    textTransform: 'none' as const,
    borderRadius: theme.spacing(1),
    transition: 'all 0.2s ease-in-out',
    cursor: loading ? 'default' : 'pointer',
    opacity: loading ? 0.7 : 1,
    // Touch-friendly minimum size for mobile
    minHeight: { xs: 44, sm: 'auto' },
    '&:disabled': {
      opacity: 1, // Keep full opacity, we'll handle it per variant
      cursor: 'not-allowed',
      transform: 'none !important',
      boxShadow: 'none !important',
      '&:hover': {
        transform: 'none !important',
        boxShadow: 'none !important',
      },
    },
  };

  // Variant-specific styles
  const variantStyles = {
    contained: {
      backgroundColor: theme.palette.primary.main,
      color: theme.palette.primary.contrastText,
      boxShadow: `0 2px 4px ${alpha(theme.palette.primary.main, 0.2)}`,
      '&:hover': {
        backgroundColor: theme.palette.primary.dark,
        boxShadow: `0 4px 8px ${alpha(theme.palette.primary.main, 0.3)}`,
        transform: 'translateY(-1px)',
      },
      '&:active': {
        transform: 'translateY(0)',
        boxShadow: `0 2px 4px ${alpha(theme.palette.primary.main, 0.2)}`,
      },
      '&:disabled': {
        backgroundColor: theme.palette.grey[300],
        color: theme.palette.grey[500],
        boxShadow: 'none',
        '&:hover': {
          backgroundColor: theme.palette.grey[300],
          boxShadow: 'none',
        },
      },
    },
    primary: {
      backgroundColor: theme.palette.primary.main,
      color: theme.palette.primary.contrastText,
      boxShadow: `0 2px 4px ${alpha(theme.palette.primary.main, 0.2)}`,
      '&:hover': {
        backgroundColor: theme.palette.primary.dark,
        boxShadow: `0 4px 8px ${alpha(theme.palette.primary.main, 0.3)}`,
        transform: 'translateY(-1px)',
      },
      '&:active': {
        transform: 'translateY(0)',
        boxShadow: `0 2px 4px ${alpha(theme.palette.primary.main, 0.2)}`,
      },
      '&:disabled': {
        backgroundColor: theme.palette.grey[300],
        color: theme.palette.grey[500],
        boxShadow: 'none',
        '&:hover': {
          backgroundColor: theme.palette.grey[300],
          boxShadow: 'none',
        },
      },
    },
    secondary: {
      backgroundColor: theme.palette.secondary.main,
      color: theme.palette.secondary.contrastText,
      boxShadow: `0 2px 4px ${alpha(theme.palette.secondary.main, 0.2)}`,
      '&:hover': {
        backgroundColor: theme.palette.secondary.dark,
        boxShadow: `0 4px 8px ${alpha(theme.palette.secondary.main, 0.3)}`,
        transform: 'translateY(-1px)',
      },
      '&:active': {
        transform: 'translateY(0)',
        boxShadow: `0 2px 4px ${alpha(theme.palette.secondary.main, 0.2)}`,
      },
      '&:disabled': {
        backgroundColor: theme.palette.grey[300],
        color: theme.palette.grey[500],
        boxShadow: 'none',
        '&:hover': {
          backgroundColor: theme.palette.grey[300],
          boxShadow: 'none',
        },
      },
    },
    outlined: {
      backgroundColor: 'transparent !important',
      color: theme.palette.primary.main,
      border: `1.5px solid ${theme.palette.primary.main}`,
      boxShadow: 'none',
      '&:hover': {
        backgroundColor: `${alpha(theme.palette.primary.main, 0.04)} !important`,
        borderColor: theme.palette.primary.dark,
        transform: 'translateY(-1px)',
        boxShadow: 'none',
      },
      '&:active': {
        transform: 'translateY(0)',
        backgroundColor: `${alpha(theme.palette.primary.main, 0.08)} !important`,
        boxShadow: 'none',
      },
      '&:focus': {
        backgroundColor: 'transparent !important',
        boxShadow: 'none',
      },
      '&:disabled': {
        backgroundColor: 'transparent !important',
        color: theme.palette.grey[400],
        borderColor: theme.palette.grey[300],
        '&:hover': {
          backgroundColor: 'transparent !important',
          borderColor: theme.palette.grey[300],
        },
      },
    },
    text: {
      backgroundColor: 'transparent',
      color: theme.palette.primary.main,
      border: 'none',
      boxShadow: 'none',
      '&:hover': {
        backgroundColor: alpha(theme.palette.primary.main, 0.04),
        transform: 'translateY(-1px)',
      },
      '&:active': {
        transform: 'translateY(0)',
        backgroundColor: alpha(theme.palette.primary.main, 0.08),
      },
      '&:disabled': {
        backgroundColor: 'transparent',
        color: theme.palette.grey[400],
        '&:hover': {
          backgroundColor: 'transparent',
        },
      },
    },
    danger: {
      backgroundColor: theme.palette.error.main,
      color: theme.palette.error.contrastText,
      boxShadow: `0 2px 4px ${alpha(theme.palette.error.main, 0.2)}`,
      '&:hover': {
        backgroundColor: theme.palette.error.dark,
        boxShadow: `0 4px 8px ${alpha(theme.palette.error.main, 0.3)}`,
        transform: 'translateY(-1px)',
      },
      '&:active': {
        transform: 'translateY(0)',
        boxShadow: `0 2px 4px ${alpha(theme.palette.error.main, 0.2)}`,
      },
      '&:disabled': {
        backgroundColor: theme.palette.grey[300],
        color: theme.palette.grey[500],
        boxShadow: 'none',
        '&:hover': {
          backgroundColor: theme.palette.grey[300],
          boxShadow: 'none',
        },
      },
    },
  };

  return {
    ...baseStyles,
    ...variantStyles[customVariant || 'contained'],
  };
});

export const Button: React.FC<CustomButtonProps> = ({
  children,
  variant,
  size = 'medium',
  loading = false,
  loadingText,
  disabled,
  icon,
  iconPosition = 'start',
  onClick,
  ...props
}) => {
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (loading || disabled) return;
    onClick?.(event);
  };

  const renderContent = () => {
    if (loading) {
      return (
        <>
          <CircularProgress
            size={size === 'small' ? 16 : size === 'large' ? 20 : 18}
            color="inherit"
            sx={{ mr: loadingText ? 1 : 0 }}
          />
          {loadingText && loadingText}
        </>
      );
    }

    return (
      <>
        {icon && iconPosition === 'start' && (
          <span style={{ marginRight: 8, display: 'flex', alignItems: 'center' }}>{icon}</span>
        )}
        {children}
        {icon && iconPosition === 'end' && (
          <span style={{ marginLeft: 8, display: 'flex', alignItems: 'center' }}>{icon}</span>
        )}
      </>
    );
  };

  return (
    <StyledButton
      variant={undefined} // Remove MUI variant to use our custom styling
      size={undefined} // Remove MUI size to use our custom sizing
      disabled={loading || disabled}
      onClick={handleClick}
      {...props}
      // Pass our custom variant to the styled component
      customVariant={variant}
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        ...props.sx,
      }}
    >
      {renderContent()}
    </StyledButton>
  );
};

// Default export
export default Button;
